/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Col, Row, Badge, Modal, ModalBody } from 'reactstrap';
// eslint-disable-next-line import/no-unresolved
import { environment } from '../../../../environment/environment';

function SmallPagination({ page, pages, setPage }) {
  if (pages <= 1) return null;
  return (
    <nav style={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
      <ul className="pagination pagination-sm" style={{ marginBottom: 0 }}>
        {page > 1 && (
          <li className="page-item">
            <div className="page-link" onClick={() => setPage(page - 1)} style={{ cursor: 'pointer' }}>
              <i className="fa fa-angle-left" />
            </div>
          </li>
        )}
        {page > 1 && (
          <li className="page-item">
            <div className="page-link" onClick={() => setPage(page - 1)} style={{ cursor: 'pointer' }}>
              {page - 1}
            </div>
          </li>
        )}
        <li className="page-item active">
          <div className="page-link">{page}</div>
        </li>
        {page < pages && (
          <li className="page-item">
            <div className="page-link" onClick={() => setPage(page + 1)} style={{ cursor: 'pointer' }}>
              {page + 1}
            </div>
          </li>
        )}
        {page < pages && (
          <li className="page-item">
            <div className="page-link" onClick={() => setPage(page + 1)} style={{ cursor: 'pointer' }}>
              <i className="fa fa-angle-right" />
            </div>
          </li>
        )}
      </ul>
    </nav>
  );
}

const ITEMS_PER_PAGE = 10;
const WITHDRAWAL_FEE = '10,00';

// KYC states Franc accepts for a withdrawal. Anything else is rejected upstream
// with a 400 that reaches us as an opaque 502, so the modal blocks it first.
// Mirrors the WhatsApp flow's gate (chatbot-service savings_validator.py:180).
export const KYC_WITHDRAWAL_ALLOWED = ['APPROVED', 'PASSED'];

/** Parse a ZAR amount that may arrive as "R2 400,00", "2400.00" or a number. */
export function parseMoney(value) {
  if (value === null || value === undefined || value === '') return 0;
  const normalised = String(value).replace(/[R\s]/g, '').replace(',', '.');
  return parseFloat(normalised) || 0;
}

/** Format a number as a ZAR amount without the R prefix, e.g. 2390 -> "2390,00". */
export function formatMoney(value) {
  return Number(value).toFixed(2).replace('.', ',');
}

/**
 * The most that can be withdrawn in one action.
 *
 * django-monolith sends `amount + fee` to Franc (savings_views.py:642). Franc
 * debits that consolidated total and pays the employee `amount`, retaining the
 * fee. So the employee receives exactly the amount entered, and the ceiling is
 * the balance less the fee — entering the full balance overdraws the account
 * and Franc rejects it with "User doesn't have sufficient funds to withdraw".
 */
export function maxWithdrawalFor(balance, fee) {
  return Math.max(parseMoney(balance) - parseMoney(fee), 0);
}

/**
 * The smallest balance from which any withdrawal can succeed.
 *
 * Two constraints apply at once: the monolith rejects `amount < fee`
 * (savings_views.py:645), and Franc rejects `amount + fee > balance`. Satisfying
 * both needs `fee <= amount <= balance - fee`, which is only possible when the
 * balance is at least twice the fee. Between one and two fees the window is
 * empty — the form would render with every amount rejected from one side or the
 * other and no way to proceed.
 */
export function minimumViableBalance(fee) {
  return parseMoney(fee) * 2;
}

/**
 * True when Franc will reject a withdrawal because KYC is incomplete.
 *
 * An empty/unknown status is not treated as a block — the field is absent on
 * some account payloads and blocking on it would strand valid withdrawals.
 */
export function isKycBlocked(kycStatus) {
  const normalised = String(kycStatus || '').toUpperCase();
  return normalised !== '' && !KYC_WITHDRAWAL_ALLOWED.includes(normalised);
}

const kycBadgeColor = (kycStatus) => {
  switch (kycStatus?.toUpperCase()) {
    case 'APPROVED':
      return 'success';
    case 'UNDETERMINED':
    case 'PENDING':
      return 'warning';
    case 'REJECTED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const subscriptionStatusBadgeColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'closed':
      return 'secondary';
    default:
      return 'secondary';
  }
};

const transactionStatusBadgeColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return 'success';
    case 'PROCESSING':
      return 'info';
    case 'PENDING':
      return 'warning';
    case 'CANCELLED':
    case 'FAILED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const transactionTypeBadgeColor = (type) => {
  switch (type?.toUpperCase()) {
    case 'DEPOSIT':
      return 'success';
    case 'WITHDRAWAL':
      return 'warning';
    default:
      return 'secondary';
  }
};

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

function formatDateDMY(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function paginate(items, page) {
  const start = (page - 1) * ITEMS_PER_PAGE;
  return items.slice(start, start + ITEMS_PER_PAGE);
}

function totalPages(items) {
  return Math.ceil(items.length / ITEMS_PER_PAGE);
}

const sectionStyle = {
  borderRadius: '4px',
  padding: '1rem 1.25rem',
  marginBottom: '1.5rem',
};

const overviewStyle = {
  ...sectionStyle,
  backgroundColor: '#f8f9fa',
};

const sectionHeadingStyle = {
  fontWeight: 'bold',
  marginBottom: '0.75rem',
  fontSize: '15px',
};

function CancelDeductionModal({ isOpen, toggle, monthlyDeduction, employeeId, onCancelled }) {
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const handleConfirm = () => {
    setCancelLoading(true);
    setCancelError(null);
    axios
      .post(`${environment.baseUrl}/savings/cancel/${employeeId}/`, {
        reason: 'employee cancelled savings',
      })
      .then(() => {
        setCancelLoading(false);
        toggle();
        if (onCancelled) onCancelled();
      })
      .catch((err) => {
        setCancelLoading(false);
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          'Failed to cancel deduction. Please try again.';
        setCancelError(msg);
      });
  };

  const handleClose = () => {
    setCancelError(null);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ fontWeight: 'bold', margin: 0, fontSize: '15px' }}>Cancel Monthly Deduction</h5>
          <button
            className="btn btn-link p-0 border-0"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
        <div className="alert alert-warning" style={{ fontSize: '0.9rem', backgroundColor: '#fff8e1', borderColor: '#ffe082', color: '#333' }}>
          ⚠️ This will stop the employee's monthly deduction of{' '}
          <strong>{monthlyDeduction}</strong>. Their existing savings balance will
          remain in their Franc account and is not affected.
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1.5rem' }}>
          This change will take effect from the next pay cycle. Any deduction
          already collected in the current cycle will not be reversed.
        </p>
        {cancelError && (
          <p className="text-danger" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
            {cancelError}
          </p>
        )}
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-outline-secondary mr-2"
            onClick={handleClose}
            disabled={cancelLoading}
          >
            Cancel
          </button>
          <button
            className="btn btn-dark"
            onClick={handleConfirm}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling…' : 'Confirm Cancellation'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

function TriggerWithdrawalModal({ isOpen, toggle, currentBalance, withdrawalFee, kycStatus, employeeId, onWithdrawn }) {
  // The balance GET is fired after the account GET and its failure is swallowed
  // as non-fatal, so `currentBalance` is null both while loading and when Franc
  // is unreachable. Treating that as R0 would assert a balance we do not have.
  const balanceUnavailable = currentBalance === null || currentBalance === undefined || currentBalance === '';
  const balanceNum = parseMoney(currentBalance);
  const feeNum = parseMoney(withdrawalFee);
  const maxWithdrawal = maxWithdrawalFor(balanceNum, feeNum);
  const kycBlocked = isKycBlocked(kycStatus);

  const [amount, setAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);

  const amountNum = parseMoney(amount);
  // The monolith enforces the same floor (savings_views.py:645).
  const belowMinimum = amountNum > 0 && amountNum < feeNum;
  const exceedsMax = amountNum > maxWithdrawal;
  // Not `maxWithdrawal <= 0` — a balance between one and two fees leaves a
  // non-zero ceiling that is still below the minimum, so every amount is
  // rejected and the form can never be submitted.
  const balanceTooLow = balanceNum < minimumViableBalance(feeNum);
  const amountToEmployee = amountNum > 0 ? formatMoney(amountNum) : '—';
  const totalDebit = amountNum > 0 ? formatMoney(amountNum + feeNum) : '—';

  const handleOpen = () => {
    setAmount(formatMoney(maxWithdrawal));
    setWithdrawError(null);
    setWithdrawSuccess(false);
  };

  const handleClose = () => {
    setWithdrawError(null);
    setWithdrawSuccess(false);
    toggle();
  };

  // Pre-fill amount when modal opens
  React.useEffect(() => {
    if (isOpen) handleOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleConfirm = () => {
    // Use the same parsed value the summary and the Confirm button are derived
    // from. Parsing the raw input again here diverged for entries like "12 00",
    // which the summary read as 1200 while the request carried 12.
    if (amountNum <= 0) {
      setWithdrawError('Please enter a valid withdrawal amount.');
      return;
    }
    if (amountNum < feeNum) {
      setWithdrawError(`Minimum withdrawal is R${formatMoney(feeNum)}.`);
      return;
    }
    if (amountNum > maxWithdrawal) {
      setWithdrawError(
        `Maximum withdrawal is R${formatMoney(maxWithdrawal)} — the R${formatMoney(feeNum)} fee is debited on top of this amount.`,
      );
      return;
    }
    setWithdrawLoading(true);
    setWithdrawError(null);
    axios
      .post(`${environment.baseUrl}/savings/withdraw/${employeeId}/`, {
        amount: amountNum.toFixed(2),
      })
      .then(() => {
        setWithdrawLoading(false);
        setWithdrawSuccess(true);
        if (onWithdrawn) onWithdrawn();
      })
      .catch((err) => {
        setWithdrawLoading(false);
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          'Failed to process withdrawal. Please try again.';
        setWithdrawError(msg);
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ fontWeight: 'bold', margin: 0, fontSize: '15px' }}>Trigger Withdrawal</h5>
          <button
            className="btn btn-link p-0 border-0"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
        {withdrawSuccess && (
          <div className="alert alert-success" style={{ fontSize: '0.9rem' }}>
            Withdrawal submitted successfully. Funds will be returned to the employee's bank account within 1–3 business days.
          </div>
        )}
        {!withdrawSuccess && kycBlocked && (
          <div className="alert alert-danger" style={{ fontSize: '0.9rem' }}>
            <strong>Withdrawals are blocked — KYC is not complete.</strong>
            <div className="mt-2">
              Franc will reject this withdrawal because the employee's KYC status is <strong>{kycStatus}</strong>.
              It must be <strong>APPROVED</strong> or <strong>PASSED</strong> before funds can be released.
            </div>
            <div className="mt-2">
              Ask the employee to complete their Franc verification, then retry. Retrying with a smaller amount will not help.
            </div>
          </div>
        )}
        {!withdrawSuccess && !kycBlocked && balanceUnavailable && (
          <div className="alert alert-warning" style={{ fontSize: '0.9rem', backgroundColor: '#fff8e1', borderColor: '#ffe082', color: '#333' }}>
            <strong>Balance unavailable.</strong>
            <div className="mt-2">
              The Franc balance for this employee could not be loaded, so the withdrawable amount is unknown.
              Close this dialog and reload the page. If it keeps failing, Franc may be unreachable — do not
              assume the balance is zero.
            </div>
          </div>
        )}
        {!withdrawSuccess && !kycBlocked && !balanceUnavailable && balanceTooLow && (
          <div className="alert alert-warning" style={{ fontSize: '0.9rem', backgroundColor: '#fff8e1', borderColor: '#ffe082', color: '#333' }}>
            <strong>Balance is too low to withdraw.</strong>
            <div className="mt-2">
              The balance is R{formatMoney(balanceNum)}. A withdrawal needs at least
              R{formatMoney(minimumViableBalance(feeNum))} — the minimum withdrawal is
              R{formatMoney(feeNum)} and the R{formatMoney(feeNum)} fee is debited on top of it.
            </div>
          </div>
        )}
        {!withdrawSuccess && !kycBlocked && !balanceUnavailable && !balanceTooLow && (
          <>
            <div className="alert alert-warning" style={{ fontSize: '0.9rem', backgroundColor: '#fff8e1', borderColor: '#ffe082', color: '#333' }}>
              ⚠️ This will withdraw funds from the employee's Franc account. This action cannot be undone. Confirm only after
              verifying the reason with the employee or employer.
            </div>
            <div className="form-group mb-3">
              <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', display: 'block' }}>
                Withdrawal amount (ZAR)
              </label>
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">R</span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0,00"
                  disabled={withdrawLoading}
                  style={{ fontSize: '0.9rem' }}
                />
              </div>
              <small className="text-muted">
                Maximum withdrawal: R{formatMoney(maxWithdrawal)}
                {' '}(balance R{formatMoney(balanceNum)} less the
                {' '}R{formatMoney(feeNum)} fee)
              </small>
            </div>
            <table className="table table-sm" style={{ marginBottom: '1rem' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold', border: 'none' }}>Amount to employee</td>
                  <td className="text-right" style={{ border: 'none', fontWeight: 'bold', color: '#28a745' }}>R{amountToEmployee}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500, border: 'none' }}>Withdrawal fee</td>
                  <td className="text-right" style={{ border: 'none', color: '#666' }}>+ R{formatMoney(feeNum)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #dee2e6' }}>
                  <td style={{ fontWeight: 'bold' }}>Total debited from savings</td>
                  <td className="text-right" style={{ fontWeight: 'bold' }}>R{totalDebit}</td>
                </tr>
              </tbody>
            </table>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              The employee receives the amount entered above; the R{formatMoney(feeNum)} fee is
              debited from their savings on top of it. Funds will be returned to the employee's bank account on file.
              Processing may take 1–3 business days via Franc.
            </p>
            {belowMinimum && (
              <p className="text-warning" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Minimum withdrawal is R{formatMoney(feeNum)}.
              </p>
            )}
            {exceedsMax && (
              <p className="text-warning" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Maximum withdrawal is R{formatMoney(maxWithdrawal)} — the
                {' '}R{formatMoney(feeNum)} fee is debited on top of this amount.
              </p>
            )}
            {withdrawError && (
              <p className="text-danger" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                {withdrawError}
              </p>
            )}
          </>
        )}
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-outline-secondary mr-2"
            onClick={handleClose}
            disabled={withdrawLoading}
          >
            {withdrawSuccess || kycBlocked || balanceUnavailable || balanceTooLow ? 'Close' : 'Cancel'}
          </button>
          {!withdrawSuccess && !kycBlocked && !balanceUnavailable && !balanceTooLow && (
            <button
              className="btn btn-dark"
              onClick={handleConfirm}
              disabled={withdrawLoading || belowMinimum || exceedsMax || amountNum <= 0}
            >
              {withdrawLoading ? 'Processing…' : 'Confirm Withdrawal'}
            </button>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}

function EmployeeSavings({ employeeId }) {
  const [accountData, setAccountData] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [investmentsPage, setInvestmentsPage] = useState(1);
  const [accountSummary, setAccountSummary] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);

  const fetchAccountData = () => {
    return axios.get(`${environment.baseUrl}/savings/account/${employeeId}/`);
  };

  const fetchBalanceData = () => {
    return axios.get(`${environment.baseUrl}/savings/balance/${employeeId}/`);
  };

  const fetchTransactions = () => {
    axios
      .get(`${environment.baseUrl}/savings/transactions/${employeeId}/`)
      .then((res) => setTransactions(res.data.transactions || []))
      .catch(() => setTransactions([]));
  };

  const fetchAccountSummary = () => {
    axios
      .get(`${environment.baseUrl}/savings/account-summary/${employeeId}/`)
      .then((res) => setAccountSummary(res.data))
      .catch(() => {
        // Account summary is non-fatal — leave as null
      });
  };

  const loadData = () => {
    setLoading(true);
    setError(null);

    fetchAccountData()
      .then((accountRes) => {
        setAccountData(accountRes.data);
        setLoading(false);

        fetchBalanceData()
          .then((balanceRes) => {
            setBalance(balanceRes.data);
          })
          .catch(() => {
            // Balance fetch failure is non-fatal — leave balance as null
          });
        fetchTransactions();
        fetchAccountSummary();
      })
      .catch((err) => {
        setLoading(false);
        if (err.response && err.response.status === 404) {
          setAccountData({ has_account: false });
        } else {
          setError('Failed to load savings data.');
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [employeeId]);

  const investments = accountData?.subscription_history || [];
  const pagedInvestments = paginate(investments, investmentsPage);
  const investmentPages = totalPages(investments);

  const activeSubscription = accountData?.active_subscription || null;
  const monthlyDeduction = activeSubscription
    ? `R${activeSubscription.amount} / month`
    : '—';
  const currentBalanceDisplay = balance
    ? `R${balance.available_balance}`
    : '—';

  const pendingInvestmentTotal = transactions
    .filter((t) => t.type?.toUpperCase() === 'DEPOSIT' && t.status?.toUpperCase() === 'PENDING')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const filteredTransactions = transactionFilter === 'all'
    ? transactions
    : transactions.filter((t) => t.type?.toUpperCase() === transactionFilter.toUpperCase());

  return (
    <div className="p-4">
      <style>{`
        .savings-btn-outline,
        .savings-btn-dark {
          transform: none !important;
          box-shadow: none !important;
        }
        .savings-btn-outline {
          border: 1px solid #d0d0d0;
          color: #062133;
          background-color: white;
        }
        .savings-btn-outline:hover {
          background-color: #f8d0d6;
          border-color: #f8d0d6;
          color: #062133;
        }
        .savings-btn-dark {
          background-color: #062133;
          border-color: #062133;
        }
        .savings-btn-dark:hover {
          background-color: #FF697F;
          border-color: #FF697F;
          color: white;
        }
      `}
      </style>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ fontWeight: 'bold', fontSize: '1.25rem', margin: 0 }}>Savings</h3>
        <div>
          <button
            className="btn mr-2 savings-btn-outline"
            onClick={() => setCancelModalOpen(true)}
          >
            Cancel Deduction
          </button>
          <button
            className="btn"
            style={{ backgroundColor: '#062133', borderColor: '#062133', color: 'white' }}
            onClick={() => setWithdrawalModalOpen(true)}
          >
            Trigger Withdrawal
          </button>
        </div>
      </div>

      {loading && <p>Loading savings data…</p>}

      {!loading && error && (
        <p className="text-danger">{error}</p>
      )}

      {!loading && !error && accountData && !accountData.has_account && (
        <p className="text-muted">No savings account found.</p>
      )}

      {!loading && !error && accountData && accountData.has_account !== false && (
        <>
          {/* Overview Section */}
          <div style={overviewStyle}>
            <h6 style={sectionHeadingStyle}>Overview</h6>
            <Row>
              <Col md={6}>
                <table className="table table-sm" style={{ marginBottom: 0 }}>
                  <tbody>
                    <tr>
                      <td className="text-muted">Franc Goal ID</td>
                      <td className="text-right" style={{ fontWeight: 500 }}>
                        {accountData.franc_goal_identifier || '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Registration Date</td>
                      <td className="text-right" style={{ fontWeight: 500 }}>
                        {formatDate(accountData.created_at)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">KYC Status</td>
                      <td className="text-right">
                        <Badge
                          color={kycBadgeColor(accountData.kyc_status)}
                          style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                        >
                          {accountData.kyc_status || '—'}
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Col>
              <Col md={6}>
                <table className="table table-sm" style={{ marginBottom: 0 }}>
                  <tbody>
                    <tr>
                      <td className="text-muted">Current Balance</td>
                      <td className="text-right" style={{ fontWeight: 'bold' }}>
                        {currentBalanceDisplay}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Pending Investment</td>
                      <td className="text-right" style={{ fontWeight: 'bold' }}>
                        {pendingInvestmentTotal > 0
                          ? `R${pendingInvestmentTotal.toFixed(2)}`
                          : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Monthly Deduction</td>
                      <td className="text-right" style={{ fontWeight: 'bold' }}>
                        {monthlyDeduction}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Deduction as % of Salary</td>
                      <td className="text-right" style={{ fontWeight: 500 }}>
                        {activeSubscription ? activeSubscription.deduction_percentage : '—'}
                      </td>
                    </tr>
                    {accountSummary?.interestEarned != null && (
                      <tr>
                        <td className="text-muted">Interest Earned</td>
                        <td className="text-right" style={{ fontWeight: 500 }}>
                          R{accountSummary.interestEarned}
                        </td>
                      </tr>
                    )}
                    {accountSummary?.closingBalance != null && (
                      <tr>
                        <td className="text-muted">Closing Balance</td>
                        <td className="text-right" style={{ fontWeight: 500 }}>
                          R{accountSummary.closingBalance}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Col>
            </Row>
          </div>

          {/* Investment History Section */}
          <div style={sectionStyle}>
            <h6 style={sectionHeadingStyle}>Investment History</h6>
            <table className="table table-sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Date</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Amount</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedInvestments.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-muted">No investment history found.</td>
                  </tr>
                ) : (
                  pagedInvestments.map((inv, index) => (
                    <tr key={index}>
                      <td>{formatDateDMY(inv.created_at)}</td>
                      <td style={{ fontWeight: 'bold' }}>R{inv.amount}</td>
                      <td>
                        <Badge
                          color={subscriptionStatusBadgeColor(inv.status)}
                          style={{
                            fontSize: '0.75rem',
                            padding: '4px 10px',
                            ...(inv.status?.toLowerCase() === 'closed' && {
                              backgroundColor: '#e9ecef',
                              color: '#6c757d',
                            }),
                          }}
                        >
                          {inv.status?.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {investmentPages > 1 && (
              <SmallPagination
                page={investmentsPage}
                pages={investmentPages}
                setPage={setInvestmentsPage}
              />
            )}
          </div>

          {/* Transaction History */}
          <div style={sectionStyle}>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '0.75rem' }}>
              <h6 style={{ ...sectionHeadingStyle, marginBottom: 0 }}>Transaction History</h6>
              <div>
                {['all', 'deposit', 'withdrawal'].map((filter) => (
                  <button
                    key={filter}
                    className={`btn btn-sm mr-1 ${transactionFilter === filter ? 'btn-dark' : 'btn-outline-secondary'}`}
                    style={{ textTransform: 'capitalize', fontSize: '0.8rem' }}
                    onClick={() => {
                      setTransactionFilter(filter);
                      setTransactionsPage(1);
                    }}
                  >
                    {filter === 'all' ? 'All' : `${filter}s`}
                  </button>
                ))}
              </div>
            </div>
            <table className="table table-sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Date</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Type</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Amount</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Status</th>
                  <th className="text-muted" style={{ fontWeight: 'normal', borderTop: 'none' }}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {paginate(filteredTransactions, transactionsPage).length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-muted">No transactions found.</td>
                  </tr>
                ) : (
                  paginate(filteredTransactions, transactionsPage).map((t, i) => (
                    <tr key={i}>
                      <td>{formatDateDMY(t.created_at)}</td>
                      <td style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>
                        {t.type?.toLowerCase() || '—'}
                      </td>
                      <td style={{ fontWeight: 'bold' }}>{t.amount ? `R${t.amount}` : '—'}</td>
                      <td>
                        <Badge
                          color={transactionStatusBadgeColor(t.status)}
                          style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                        >
                          {t.status || '—'}
                        </Badge>
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.8rem' }}>{t.reference || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {totalPages(filteredTransactions) > 1 && (
              <SmallPagination
                page={transactionsPage}
                pages={totalPages(filteredTransactions)}
                setPage={setTransactionsPage}
              />
            )}
          </div>
        </>
      )}

      <TriggerWithdrawalModal
        isOpen={withdrawalModalOpen}
        toggle={() => setWithdrawalModalOpen(false)}
        // null (not '0') while the balance is loading or after a failed fetch,
        // so the modal can say "unavailable" rather than claim a zero balance.
        currentBalance={balance ? balance.available_balance : null}
        withdrawalFee={WITHDRAWAL_FEE}
        // /savings/balance/ refreshes KYC from Franc on every call, so prefer it
        // over the cached value on /savings/account/.
        kycStatus={balance?.kyc_status || accountData?.kyc_status || ''}
        employeeId={employeeId}
        onWithdrawn={loadData}
      />
      <CancelDeductionModal
        isOpen={cancelModalOpen}
        toggle={() => setCancelModalOpen(false)}
        monthlyDeduction={monthlyDeduction}
        employeeId={employeeId}
        onCancelled={loadData}
      />
    </div>
  );
}

export default EmployeeSavings;
